import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { Membership, MembershipStatus } from '../../api/types';

const STATUS_LABEL: Record<MembershipStatus, string> = {
  NEW: 'Nuova',
  HANDLED: 'Gestita',
  ARCHIVED: 'Archiviata',
};

const STATUS_STYLE: Record<MembershipStatus, string> = {
  NEW: 'bg-[#E4FF1A] text-[#0B0B0C]',
  HANDLED: 'bg-emerald-100 text-emerald-800',
  ARCHIVED: 'bg-neutral-200 text-neutral-600',
};

const FILTERS: { key: 'ALL' | MembershipStatus; label: string }[] = [
  { key: 'ALL', label: 'Tutte' },
  { key: 'NEW', label: 'Nuove' },
  { key: 'HANDLED', label: 'Gestite' },
  { key: 'ARCHIVED', label: 'Archiviate' },
];

const MembershipsAdmin: React.FC = () => {
  const [items, setItems] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'ALL' | MembershipStatus>('ALL');

  const load = () => {
    setLoading(true);
    adminApi.memberships.list().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (id: number, status: MembershipStatus) => {
    const updated = await adminApi.memberships.setStatus(id, status);
    setItems((list) => list.map((m) => (m.id === id ? updated : m)));
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminare definitivamente questa richiesta?')) return;
    await adminApi.memberships.remove(id);
    setItems((list) => list.filter((m) => m.id !== id));
  };

  const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

  const shown = filter === 'ALL' ? items : items.filter((m) => m.status === filter);
  const newCount = items.filter((m) => m.status === 'NEW').length;

  if (loading) return <p className="text-neutral-500">Caricamento…</p>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Iscrizioni & Tesseramenti</h1>
        <p className="text-neutral-500 text-sm">
          Richieste inviate dal form pubblico “Iscrizioni”.
          {newCount > 0 && <span className="text-[#0B0B0C] font-medium"> · {newCount} nuove da gestire</span>}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded text-sm border transition ${filter === f.key ? 'bg-[#0B0B0C] text-white border-[#0B0B0C]' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="text-neutral-500">Nessuna richiesta.</p>
      ) : (
        <div className="space-y-3">
          {shown.map((m) => (
            <div key={m.id} className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{m.firstName} {m.lastName}</p>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[m.status]}`}>{STATUS_LABEL[m.status]}</span>
                  </div>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    <a href={`mailto:${m.email}`} className="text-blue-600 hover:underline">{m.email}</a>
                    {m.phone && <> · <a href={`tel:${m.phone}`} className="hover:underline">{m.phone}</a></>}
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">Ricevuta il {fmt(m.createdAt)}</p>
                  {m.message && <p className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap bg-neutral-50 border border-neutral-100 rounded p-3">{m.message}</p>}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="flex gap-2">
                    {m.status !== 'HANDLED' && <button onClick={() => setStatus(m.id, 'HANDLED')} className="text-sm text-emerald-600 hover:underline">Segna gestita</button>}
                    {m.status === 'HANDLED' && <button onClick={() => setStatus(m.id, 'NEW')} className="text-sm text-neutral-500 hover:underline">Riapri</button>}
                    {m.status !== 'ARCHIVED' && <button onClick={() => setStatus(m.id, 'ARCHIVED')} className="text-sm text-neutral-500 hover:underline">Archivia</button>}
                  </div>
                  <button onClick={() => remove(m.id)} className="text-sm text-red-500 hover:underline">Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MembershipsAdmin;
